import * as React from "react";
import { Document, PDFViewer, Page } from "@react-pdf/renderer";
import { getAbbreviationForProductionJobMethod } from "../../helpers/getAbbreviationForProductionJobMethod";
import { getAbbreviationForProductionJobMethodLocation } from "../../helpers/getAbbreviationForProductionJobMethodLocation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import ReceivingLabel from "./components/ReceivingLabel";

const ReceivingLabelViewer = ({
  autoPrint,
  brand,
  createdStockContainerId,
  customerName,
  dueBy,
  express,
  hidden,
  isArtworkApproved,
  isResolution,
  orderNumber,
  printRef,
  priority,
  productionDate,
  productionJobs,
  productionStationAssignment,
  style,
  totalProductionJobs
}) => {
  const [shouldAutoPrint, setShouldAutoPrint] = useState(autoPrint || false);
  const [rendered, setRendered] = useState(false);
  const [ready, setReady] = useState(false);
  const [stockContainerBarcodes, setStockContainerBarcodes] = useState(null);

  const modifiedProductionJobs = useMemo(() => {
    if (productionJobs) {
      return productionJobs.map(productionJob => ({
        ...productionJob,
        printLocationsByMethod: productionJob.customizationAreas.reduce(
          (accumulator, customizationArea) => {
            const method = getAbbreviationForProductionJobMethod(
              customizationArea.method
            );
            const location = getAbbreviationForProductionJobMethodLocation(
              customizationArea.location
            );
            const a = accumulator.find(i => i.method === method);
            if (a) {
              a.locations.push(location);
            } else {
              accumulator.push({
                method: method,
                locations: [location]
              });
            }
            return accumulator;
          },
          []
        )
      }));
    }

    return [];
  }, [productionJobs]);

  const computedStyle = useMemo(() => {
    return hidden ? { ...style, display: "none" } : style;
  }, [hidden, style]);

  // Handlers:
  const pdfRef = useRef(null);
  const handlePdfRef = ref => {
    if(pdfRef.current === null) {
      pdfRef.current = ref;
    }
  }

  const handlePrint = useCallback(() => {
    if (rendered && pdfRef && pdfRef.current) {
      pdfRef.current.contentWindow.focus();
      pdfRef.current.contentWindow.print();
    }
  }, [pdfRef, rendered]);

  const handleRender = useCallback(() => {
    console.log("handleRender");
    setTimeout(() => {
      setRendered(true);
      console.log("handleRender - shouldAutoPrint", shouldAutoPrint);
      if (shouldAutoPrint) {
        console.log("handleRender - printing");
        console.log("handleRender - pdfRef", pdfRef);
        console.log("handleRender - pdfRef.current", pdfRef.current);
        console.log("handleRender - pdfRef.current.contentWindow", pdfRef.current.contentWindow);
        pdfRef.current.contentWindow.focus();
        pdfRef.current.contentWindow.print();
        setTimeout(() => {
          setShouldAutoPrint(false);
          window.focus();
        }, 2000);
        //setShouldAutoPrint(false);
      }
    }, 1000);
  }, [shouldAutoPrint]);

  useEffect(() => {
    if (createdStockContainerId) {
      QRCode.toDataURL(
        JSON.stringify({
          variant: "OBJECT",
          type: "StockContainer",
          id: createdStockContainerId
        })
      ).then(barcode => {
        setStockContainerBarcodes({ [createdStockContainerId]: barcode });
        setReady(true);
      });
    } else if (modifiedProductionJobs && !createdStockContainerId) {
      const stockContainers = modifiedProductionJobs
        .map(({ stockContainers }) => stockContainers.map(({ id }) => id))
        .flat();
      const stockContainerBarcodePromises = stockContainers.map(id => {
        return QRCode.toDataURL(
          JSON.stringify({
            variant: "OBJECT",
            type: "StockContainer",
            id: id
          })
        ).then(barcode => ({
          id,
          barcode
        }));
      });
      Promise.all(stockContainerBarcodePromises).then(stockContainers => {
        const stockContainerBarcodes = stockContainers.reduce(
          (accumulator, { barcode, id }) => {
            if (barcode) {
              accumulator[id] = barcode;
              return accumulator;
            }
            return null;
          },
          {}
        );
        setStockContainerBarcodes(stockContainerBarcodes);
        setReady(true);
      });
    }
  }, [createdStockContainerId, modifiedProductionJobs]);

  useEffect(() => {
    if (printRef) {
      printRef(handlePrint);
    }
  }, [printRef, handlePrint, rendered]);

  if (ready) {
    return (
      <PDFViewer
        innerRef={handlePdfRef}
        style={computedStyle}
        width="100%"
        height="1000"
      >
        <Document onRender={handleRender}>
          {modifiedProductionJobs.map(productionJob => (
            <React.Fragment key={productionJob.id}>
              {productionJob.stockContainers.map(stockContainer => (
                <React.Fragment key={stockContainer.id + "-wrapper"}>
                  {(!createdStockContainerId ||
                    createdStockContainerId === stockContainer.id) && (
                    <Page size={[288, 166.5]} key={stockContainer.id}>
                      <ReceivingLabel
                        express={express}
                        productionJob={productionJob}
                        customerName={customerName}
                        brand={brand}
                        isResolution={isResolution}
                        priority={priority}
                        isArtworkApproved={isArtworkApproved}
                        orderNumber={orderNumber}
                        barcode={stockContainerBarcodes[stockContainer.id]}
                        productionDate={productionDate}
                        dueBy={dueBy}
                        totalProductionJobs={totalProductionJobs}
                        productionStationAssignment={
                          productionStationAssignment
                        }
                        lineItemsOld={productionJob.lineItemBreakdownByProduct
                          .map(({ product: { style }, lineItems }) => {
                            return lineItems
                              .map(({ sizes, color: { name: color } }) => {
                                return sizes.map(
                                  ({ quantity, size: { code: size } }) => {
                                    return {
                                      identifier: "GTIN", // TODO:
                                      countryOfOrigin: "CN", // TODO:
                                      description: `${style}-${color}-${size}`,
                                      quantity: quantity
                                    };
                                  }
                                );
                              })
                              .reduce(
                                (accumulator, value) => [
                                  ...accumulator,
                                  ...value
                                ],
                                []
                              );
                          })
                          .reduce(
                            (accumulator, value) => [...accumulator, ...value],
                            []
                          )}
                      />
                    </Page>
                  )}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </Document>
      </PDFViewer>
    );
  }
  return <div />;
};

export default ReceivingLabelViewer;
